import { type InfiniteData, useQueryClient } from "@tanstack/react-query";
import { unsupportedMediaReason } from "@wcp/shared";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { ApiError } from "../../lib/api.js";
import { avatarStyle, initialsFor } from "../../lib/avatar.js";
import { useBusiness } from "../../lib/business.js";
import { useSse } from "../../lib/sse.js";
import { useRenameContact } from "../contacts/api.js";
import { useTemplates } from "../templates/api.js";
import {
  type AddTrackingBody,
  type ConversationPage,
  type ConversationRow,
  type LabelRow,
  type LogOrderBody,
  type MessageAdReferral,
  markConversationRead,
  type OrderRow,
  type ThreadMessage,
  useAddTracking,
  useApplyLabel,
  useConversation,
  useConversations,
  useDeleteLabel,
  useDeleteOrder,
  useLabels,
  useLogOrder,
  useOrders,
  useReact,
  useRemoveLabel,
  useRenameLabel,
  useSendMessage,
  useThread,
  useUpdateOrder,
  useUploadMedia,
  type WindowState,
} from "./api.js";
import { EmojiPicker } from "./EmojiPicker.js";
import styles from "./InboxScreen.module.css";

/* ------------------------------------------------------------------ *
 * Inbox — ported 1:1 from the prototype "Inbox / Thread" component.
 * The app shell (TopBar/NavRail/MobileNav) is already rebuilt; only this
 * feature's internal DOM/CSS is brought to parity here.
 *
 * AI is OFF (flag off), so the bot/human chrome (mode pill, "needs human"
 * tags, the bot bubble class, the "Handled by…" kebab item, the bot hint)
 * is intentionally stripped. The customer-context rail body + NewChatModal
 * create flow are Phase-4 (no data on staging) — the chrome renders around
 * empty states.
 * ------------------------------------------------------------------ */

type Filter = "all" | "unread" | `label:${string}`;

/* ---- Inline icon set (exact viewBoxes/paths from the prototype) ------ */
const Ic = {
  search: (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <circle cx="9" cy="9" r="5.5" stroke="currentColor" strokeWidth="1.5" />
      <path d="M13.5 13.5l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  ),
  plus: (
    <svg viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path d="M7 2v10M2 7h10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  ),
  chevron: (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M5.5 8l4.5 4.5L14.5 8"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  back: (
    <svg viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path
        d="M11 7H3M7 3L3 7l4 4"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="square"
      />
    </svg>
  ),
  send: (
    <svg viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path d="M3 7h8M7 3l4 4-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="square" />
    </svg>
  ),
  paperclip: (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M13.7 6.3l-5.9 5.9a1.7 1.7 0 002.4 2.4l5.9-5.9a3.3 3.3 0 00-4.7-4.7l-6 6a4.9 4.9 0 006.9 6.9l4.3-4.3"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  receipt: (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M5 3.5h10v13l-2-1.2-2 1.2-2-1.2-2 1.2V3.5z"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
      <path d="M8 7.5h4M8 10.5h4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ),
  tag: (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M10.4 3H4.5A1.5 1.5 0 003 4.5v5.9a1.5 1.5 0 00.44 1.06l5.6 5.6a1.5 1.5 0 002.12 0l4.94-4.94a1.5 1.5 0 000-2.12l-5.6-5.6A1.5 1.5 0 0010.4 3z"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
      <circle cx="6.9" cy="6.9" r="1.05" fill="currentColor" />
    </svg>
  ),
  contacts: (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <circle cx="7.5" cy="7" r="2.8" stroke="currentColor" strokeWidth="1.4" />
      <path
        d="M2.5 16c.6-3 2.6-4.5 5-4.5s4.4 1.5 5 4.5M14 7h3.5M14 10h3.5"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  ),
  kebab: (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <circle cx="10" cy="4.5" r="1.45" fill="currentColor" />
      <circle cx="10" cy="10" r="1.45" fill="currentColor" />
      <circle cx="10" cy="15.5" r="1.45" fill="currentColor" />
    </svg>
  ),
  personFill: (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="8.8" r="4" fill="currentColor" />
      <path d="M3.6 21.5c0-4.7 3.7-7.3 8.4-7.3s8.4 2.6 8.4 7.3z" fill="currentColor" />
    </svg>
  ),
  adperf: (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M3 17h14" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <rect x="4" y="10" width="3" height="5" rx="0.6" stroke="currentColor" strokeWidth="1.4" />
      <rect
        x="8.5"
        y="6.5"
        width="3"
        height="8.5"
        rx="0.6"
        stroke="currentColor"
        strokeWidth="1.4"
      />
      <rect
        x="13"
        y="3.5"
        width="3"
        height="11.5"
        rx="0.6"
        stroke="currentColor"
        strokeWidth="1.4"
      />
    </svg>
  ),
  clock: (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <circle cx="10" cy="10" r="7" stroke="currentColor" strokeWidth="1.4" />
      <path
        d="M10 6v4.2l2.8 1.8"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  inbox: (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M2.5 11h3.2l1.3 2h6l1.3-2h3.2M2.5 11l2-6.5h11l2 6.5v4.5a1.5 1.5 0 01-1.5 1.5h-13A1.5 1.5 0 012.5 15.5z"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
    </svg>
  ),
};

/* Initials on a stable per-contact colour when we know a name; the person glyph
   otherwise. WhatsApp's Cloud API does not expose customer profile photos, so
   initials are the closest honest stand-in. */
function Avatar({ size = 38, name, seed }: { size?: number; name?: string | null; seed?: string }) {
  const initials = initialsFor(name);
  if (!initials) {
    return (
      <div className={styles.av} style={{ width: size, height: size }} aria-hidden="true">
        {Ic.personFill}
      </div>
    );
  }
  return (
    <div
      className={`${styles.av} ${styles.avInitials}`}
      style={{
        width: size,
        height: size,
        fontSize: size * 0.4,
        ...avatarStyle(seed ?? name ?? ""),
      }}
      aria-hidden="true"
    >
      {initials}
    </div>
  );
}

/** Asia/Kuala_Lumpur clock time / "Yesterday" / short date for a row timestamp. */
function fmtRowTime(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const tz = "Asia/Kuala_Lumpur";
  const dayKey = (x: Date) => x.toLocaleDateString("en-CA", { timeZone: tz });
  const today = dayKey(now);
  const y = new Date(now);
  y.setDate(now.getDate() - 1);
  if (dayKey(d) === today)
    return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: tz });
  if (dayKey(d) === dayKey(y)) return "Yesterday";
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", timeZone: tz });
}

function fmtClock(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Asia/Kuala_Lumpur",
  });
}

export function InboxScreen() {
  const { activeId } = useBusiness();
  return <BusinessInbox key={activeId} businessId={activeId} />;
}

function BusinessInbox({ businessId: activeId }: { businessId: string }) {
  const qc = useQueryClient();
  useSse(activeId, qc);
  const [selected, setSelected] = useState<number | null>(null);
  const [search, setSearch] = useState("");
  const [filter, setFilterState] = useState<Filter>("all");
  const [info, setInfo] = useState(false); // desktop context rail open
  // Mobile is list-first: the list shows until a conversation is tapped, then the
  // thread takes over (≤980px). data-mobile-view also drives the MobileNav hide
  // rule (body:has([data-mobile-view="thread"])) so the bottom bar steps aside.
  const [mobileView, setMobileView] = useState<"list" | "thread">("list");

  const [debouncedSearch, setDebouncedSearch] = useState("");
  // Trim a cached list back to page 1 before its observer (re)mounts, so switching
  // search/filter or returning to the inbox refetches one page, not every page loaded
  // last time. Synchronous: the target key has no observer yet, so nothing to cancel.
  const resetPages = useCallback(
    (q: string, nextFilter: Filter) => {
      const queryKey = [activeId, "conversations", { q, filter: nextFilter }];
      qc.setQueryData<InfiniteData<ConversationPage>>(queryKey, (old) =>
        old
          ? {
              ...old,
              pages: old.pages.slice(0, 1),
              pageParams: old.pageParams.slice(0, 1),
            }
          : old,
      );
    },
    [activeId, qc],
  );
  // Layout effects run before useConversations subscribes (a passive effect), so the
  // stale-on-mount refetch covers page 1 only. Once per mount: keyed by business.
  useLayoutEffect(() => resetPages("", "all"), [resetPages]);
  function setFilter(next: Filter) {
    if (next === filter) return;
    resetPages(debouncedSearch, next);
    setFilterState(next);
  }
  useEffect(() => {
    const timer = window.setTimeout(() => {
      const q = search.trim();
      if (q === debouncedSearch) return;
      resetPages(q, filter);
      setDebouncedSearch(q);
    }, 250);
    return () => window.clearTimeout(timer);
  }, [search, debouncedSearch, filter, resetPages]);
  const convos = useConversations(activeId, { q: debouncedSearch, filter });
  const list = useMemo(() => {
    const seen = new Set<number>();
    return (convos.data?.pages.flatMap((p) => p.conversations) ?? []).filter((c) => {
      if (seen.has(c.id)) return false;
      seen.add(c.id);
      return true;
    });
  }, [convos.data]);
  const page = convos.data?.pages[0];
  const unreadCount = page?.meta?.unread ?? 0;
  const usedLabels = page?.meta?.labels_in_use.map((l) => l.name) ?? [];
  const detail = useConversation(activeId, selected);
  const active = detail.data ?? null;
  useEffect(() => {
    if (detail.error instanceof ApiError && detail.error.status === 404) {
      setSelected(null);
      setMobileView("list");
    }
  }, [detail.error]);

  function openConvo(c: ConversationRow) {
    qc.setQueryData([activeId, "conversation", c.id], c);
    setSelected(c.id);
    setMobileView("thread");
    if (c.unread_count > 0) {
      markConversationRead(c.id).then(() => {
        qc.invalidateQueries({ queryKey: [activeId, "conversations"] });
        qc.invalidateQueries({ queryKey: [activeId, "conversation", c.id] });
      });
    }
  }

  const splitClass = [
    styles.split,
    active ? styles.hasThread : "",
    active && info ? styles.hasInfo : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={styles.inbox} data-mobile-view={mobileView}>
      <div className={splitClass}>
        <div className={styles.listPane}>
          <div className={styles.stick}>
            <div className={styles.listHead}>
              <div className={styles.headLeft}>
                <span className={styles.headTitle}>Inbox</span>
                <span className={styles.headMeta}>
                  {page?.meta?.active ?? 0} chat{page?.meta?.active === 1 ? "" : "s"}
                </span>
              </div>
              <button
                type="button"
                className={styles.newChatBtn}
                disabled
                title="Business-initiated chats arrive in Phase 4"
              >
                {Ic.plus} New
              </button>
            </div>

            <div className={styles.searchWrap}>
              <div className={styles.searchbox}>
                {Ic.search}
                <input
                  placeholder="Search chats…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                {search && (
                  <button
                    type="button"
                    className={styles.searchClear}
                    aria-label="Clear search"
                    onClick={() => setSearch("")}
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>

            <InboxFilters
              filter={filter}
              setFilter={setFilter}
              unreadCount={unreadCount}
              labels={usedLabels}
            />
          </div>

          {convos.isPending && <div className={styles.emptyList}>Loading chats…</div>}
          {convos.isError && !convos.data && (
            <div className={styles.emptyList} role="alert">
              Could not load chats.{" "}
              <button type="button" onClick={() => convos.refetch()}>
                Retry
              </button>
            </div>
          )}
          {convos.data && list.length === 0 && (
            <div className={styles.emptyList}>
              {page?.meta?.active === 0 ? "No conversations yet." : "No chats match."}
            </div>
          )}
          {list.map((c) => (
            <button
              type="button"
              key={c.id}
              className={[
                styles.conv,
                c.id === selected ? styles.sel : "",
                c.unread_count > 0 ? styles.hasUnread : "",
              ]
                .filter(Boolean)
                .join(" ")}
              onClick={() => openConvo(c)}
            >
              <Avatar name={c.name ?? c.wa_name} seed={c.phone} />
              <div className={styles.convBody}>
                <div className={styles.convName}>{c.name ?? c.wa_name ?? c.phone}</div>
                <div className={styles.convSnip}>{c.last_message_preview ?? "—"}</div>
                {(c.labels?.length ?? 0) > 0 && (
                  <div className={styles.convLabels}>
                    {c.labels?.map((l) => (
                      <span className={styles.convLabel} key={l}>
                        {l}
                      </span>
                    ))}
                  </div>
                )}
                {c.is_ctwa && (
                  <div className={styles.convAd}>
                    {Ic.adperf}
                    <span>{c.referral_ad_name ?? "From ad"}</span>
                  </div>
                )}
              </div>
              <div className={styles.convRight}>
                <span className={styles.convTime}>{fmtRowTime(c.last_activity_at)}</span>
                {c.unread_count > 0 && <span className={styles.unread}>{c.unread_count}</span>}
              </div>
            </button>
          ))}
          {page && (
            <div className={styles.listPaging}>
              <span>
                Showing {list.length} of {page.total}
              </span>
              {convos.isFetchNextPageError && <span role="alert">Couldn't load more chats.</span>}
              {convos.hasNextPage && (
                <button
                  type="button"
                  disabled={convos.isFetching || search.trim() !== debouncedSearch}
                  onClick={() => convos.fetchNextPage({ cancelRefetch: false })}
                >
                  {convos.isFetchingNextPage ? "Loading…" : "Load more"}
                </button>
              )}
            </div>
          )}
        </div>

        <div className={styles.threadPane}>
          {active ? (
            <Thread
              key={active.id}
              businessId={activeId}
              convo={active}
              onBack={() => {
                setMobileView("list");
                setSelected(null);
              }}
              infoActive={info}
              onInfo={() => setInfo((v) => !v)}
            />
          ) : (
            <div className={styles.threadEmpty}>
              <div className={styles.threadEmptyIc}>{Ic.inbox}</div>
              <div>Select a conversation to view the thread</div>
            </div>
          )}
        </div>

        {active && info && (
          <aside className={styles.ctx}>
            <div className={styles.ctxHead}>
              <span className={styles.ctxEyebrow}>Customer</span>
              <button
                type="button"
                className={styles.ctxClose}
                aria-label="Hide panel"
                onClick={() => setInfo(false)}
              >
                ✕
              </button>
            </div>
            <div className={styles.ctxBody}>
              <ContactProfile convo={active} businessId={activeId} />
            </div>
          </aside>
        )}
      </div>
    </div>
  );
}

/* Single-row filter bar: All + Unread are pinned; label chips fill the rest of
   the row and any that don't fit collapse into a "More ▾" dropdown (measured
   against the live row width, recomputed on resize). */
function InboxFilters({
  filter,
  setFilter,
  unreadCount,
  labels,
}: {
  filter: Filter;
  setFilter: (f: Filter) => void;
  unreadCount: number;
  labels: string[];
}) {
  const rowRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const moreRef = useRef<HTMLDivElement>(null);
  const [visN, setVisN] = useState(labels.length);
  const [moreOpen, setMoreOpen] = useState(false);

  useLayoutEffect(() => {
    function recompute() {
      const row = rowRef.current;
      const meas = measureRef.current;
      if (!row || !meas) return;
      const avail = row.clientWidth;
      const kids = Array.from(meas.children) as HTMLElement[]; // [All, Unread, ...labels, More]
      if (kids.length < 2) return;
      const gap = 6;
      const allW = kids[0]?.offsetWidth ?? 0;
      const unreadW = kids[1]?.offsetWidth ?? 0;
      const moreW = kids[kids.length - 1]?.offsetWidth ?? 0;
      let used = allW + gap + unreadW;
      let n = 0;
      for (let i = 0; i < labels.length; i++) {
        const w = kids[2 + i]?.offsetWidth ?? 0;
        const needMore = i < labels.length - 1;
        if (used + gap + w + (needMore ? gap + moreW : 0) <= avail) {
          used += gap + w;
          n++;
        } else break;
      }
      setVisN(n);
    }
    recompute();
    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== "undefined" && rowRef.current) {
      ro = new ResizeObserver(recompute);
      ro.observe(rowRef.current);
    } else {
      window.addEventListener("resize", recompute);
    }
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", recompute);
    };
  }, [labels]);

  useEffect(() => {
    if (!moreOpen) return;
    const onDown = (e: MouseEvent) => {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) setMoreOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [moreOpen]);

  const visible = labels.slice(0, visN);
  const overflow = labels.slice(visN);
  const activeLabel = filter.startsWith("label:") ? filter.slice(6) : null;
  const moreActive = activeLabel != null && overflow.includes(activeLabel);

  return (
    <div className={styles.filters}>
      <div className={styles.filtersRow} ref={rowRef}>
        <button
          type="button"
          className={[styles.chip, filter === "all" ? styles.chipOn : ""].filter(Boolean).join(" ")}
          onClick={() => setFilter("all")}
        >
          All
        </button>
        <button
          type="button"
          className={[styles.chip, filter === "unread" ? styles.chipOn : ""]
            .filter(Boolean)
            .join(" ")}
          onClick={() => setFilter("unread")}
        >
          Unread{unreadCount > 0 && <span className={styles.chipCt}>{unreadCount}</span>}
        </button>
        {visible.map((l) => (
          <button
            type="button"
            key={l}
            className={[styles.chip, filter === `label:${l}` ? styles.chipOn : ""]
              .filter(Boolean)
              .join(" ")}
            onClick={() => setFilter(`label:${l}`)}
          >
            {l}
          </button>
        ))}
        {overflow.length > 0 && (
          <div className={styles.filterMoreWrap} ref={moreRef}>
            <button
              type="button"
              className={[styles.chip, styles.chipMore, moreActive ? styles.chipOn : ""]
                .filter(Boolean)
                .join(" ")}
              onClick={() => setMoreOpen((o) => !o)}
              aria-expanded={moreOpen}
              aria-label="More filters"
            >
              {Ic.chevron}
            </button>
            {moreOpen && (
              <div className={styles.filterMoreMenu} role="menu">
                {overflow.map((l) => (
                  <button
                    type="button"
                    key={l}
                    className={[styles.moreOpt, filter === `label:${l}` ? styles.moreOptOn : ""]
                      .filter(Boolean)
                      .join(" ")}
                    onClick={() => {
                      setFilter(`label:${l}`);
                      setMoreOpen(false);
                    }}
                  >
                    {l}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      <div className={styles.filtersMeasure} ref={measureRef} aria-hidden="true">
        <button type="button" className={styles.chip}>
          All
        </button>
        <button type="button" className={styles.chip}>
          Unread{unreadCount > 0 && <span className={styles.chipCt}>{unreadCount}</span>}
        </button>
        {labels.map((l) => (
          <button type="button" key={l} className={styles.chip}>
            {l}
          </button>
        ))}
        <button type="button" className={[styles.chip, styles.chipMore].join(" ")}>
          {Ic.chevron}
        </button>
      </div>
    </div>
  );
}

function Ticks({ status }: { status: string }) {
  // WhatsApp double-tick; read state tints azure. Failed shows a heritage "!".
  if (status === "failed") return <span className={styles.tickFail}>!</span>;
  return (
    <span className={styles.ticks} data-read={status === "read"} title="Delivered">
      <svg viewBox="0 0 18 12" fill="none" aria-hidden="true">
        <path
          d="M1 6.5l3 3 6.5-8M6.5 9.5l3-3.2M9.5 9.5l6.5-8"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );
}

const REACTIONS = ["👍", "❤️", "😂", "😮", "😢", "🙏"];

function humanSize(n?: number | null): string {
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function kindOf(mime: string): "image" | "video" | "audio" | "document" {
  if (mime.startsWith("image/")) return "image";
  if (mime.startsWith("video/")) return "video";
  if (mime.startsWith("audio/")) return "audio";
  return "document";
}

/**
 * Bake EXIF orientation into the pixels for JPEGs before upload. Phone photos
 * (e.g. iPhone) store rotated pixels + an orientation tag; browsers honour it but
 * the WhatsApp Cloud API shows the raw pixels, so images arrive rotated. Re-drawing
 * through a canvas with `imageOrientation:"from-image"` bakes the rotation in and
 * drops the tag. Non-JPEG or any failure → return the original untouched.
 */
/** A file in the compose tray; mediaId is set once its upload has been stored. */
type PendingMedia = { file: File; url: string; mediaId?: number };

async function normalizeImageOrientation(file: File): Promise<File> {
  if (file.type !== "image/jpeg" && file.type !== "image/jpg") return file;
  try {
    const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
    const canvas = document.createElement("canvas");
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      bitmap.close();
      return file;
    }
    ctx.drawImage(bitmap, 0, 0);
    bitmap.close();
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", 0.92),
    );
    if (!blob) return file;
    return new File([blob], file.name.replace(/\.[^.]+$/, ".jpg"), { type: "image/jpeg" });
  } catch {
    return file;
  }
}

/** WhatsApp-style circular upload progress ring, overlaid on the media. */
function UploadRing({ progress }: { progress: number }) {
  const C = 2 * Math.PI * 15;
  return (
    <div className={styles.uploadOverlay}>
      <svg className={styles.uploadRing} viewBox="0 0 36 36" aria-hidden="true">
        <circle className={styles.ringTrack} cx="18" cy="18" r="15" />
        <circle
          className={styles.ringFill}
          cx="18"
          cy="18"
          r="15"
          style={{ strokeDasharray: C, strokeDashoffset: C * (1 - progress / 100) }}
        />
      </svg>
    </div>
  );
}

/** Render a message's media/structured payload (image, video, audio, voice,
 *  document, sticker, location, contact) to match WhatsApp. A single element
 *  serves the whole optimistic lifecycle: while uploading it paints the local
 *  object-URL with a progress ring, then swaps its `src` in place to the served
 *  copy — so there is never a second bubble to double or jump. */
function MediaContent({
  m,
  onOpenImage,
  onMediaLoad,
}: {
  m: ThreadMessage;
  onOpenImage: (src: string) => void;
  onMediaLoad: () => void;
}) {
  // Prefer the local object-URL (optimistic) and fall back to the served copy.
  const src = m.localUrl ?? (m.mediaId ? `/api/v1/media/${m.mediaId}` : null);
  const kind = m.mediaKind ?? m.type;
  const uploading = m.uploadProgress != null;
  if (src && kind === "image") {
    const img = (
      <img
        className={styles.mediaImg}
        src={src}
        alt={m.body ?? "Photo"}
        loading="lazy"
        onLoad={onMediaLoad}
      />
    );
    if (uploading) {
      return (
        <div className={styles.uploadingWrap}>
          {img}
          <UploadRing progress={m.uploadProgress ?? 0} />
        </div>
      );
    }
    return (
      <button type="button" className={styles.mediaImgBtn} onClick={() => onOpenImage(src)}>
        {img}
      </button>
    );
  }
  if (src && kind === "video") {
    // The `#t=0.1` fragment makes the browser render the frame at 0.1s as a poster
    // (served copy supports range requests) instead of a blank grey box.
    const videoSrc = uploading ? src : `${src}#t=0.1`;
    return (
      <div className={styles.uploadingWrap}>
        <video
          className={styles.mediaVideo}
          src={videoSrc}
          controls={!uploading}
          muted={uploading}
          playsInline
          preload="metadata"
          onLoadedMetadata={onMediaLoad}
        />
        {uploading && <UploadRing progress={m.uploadProgress ?? 0} />}
      </div>
    );
  }
  if ((kind === "audio" || kind === "document") && uploading) {
    return (
      <div className={styles.docCard}>
        <span className={styles.docIcon} aria-hidden="true">
          📎
        </span>
        <span className={styles.docMeta}>
          <span className={styles.docName}>{m.mediaFilename ?? "Uploading…"}</span>
          <span className={styles.docSize}>Uploading… {m.uploadProgress ?? 0}%</span>
        </span>
      </div>
    );
  }
  if (src && kind === "sticker") {
    return (
      <img
        className={styles.stickerImg}
        src={src}
        alt="Sticker"
        loading="lazy"
        onLoad={onMediaLoad}
      />
    );
  }
  if (src && kind === "audio") {
    return (
      <div className={styles.audioRow}>
        {m.mediaVoice ? <span aria-hidden="true">🎤</span> : null}
        {/* biome-ignore lint/a11y/useMediaCaption: voice notes have no caption track */}
        <audio className={styles.audioPlayer} src={src} controls preload="metadata" />
      </div>
    );
  }
  if (src && kind === "document") {
    return (
      <a className={styles.docCard} href={`/api/v1/media/${m.mediaId}/download`}>
        <span className={styles.docIcon} aria-hidden="true">
          📄
        </span>
        <span className={styles.docMeta}>
          <span className={styles.docName}>{m.mediaFilename ?? "Document"}</span>
          <span className={styles.docSize}>{humanSize(m.mediaSize)}</span>
        </span>
      </a>
    );
  }
  if (m.type === "location") {
    const loc = m.payload as
      | { latitude?: number; longitude?: number; name?: string; address?: string }
      | null
      | undefined;
    const q = loc?.latitude != null ? `${loc.latitude},${loc.longitude}` : "";
    return (
      <a
        className={styles.locCard}
        href={`https://maps.google.com/?q=${q}`}
        target="_blank"
        rel="noreferrer"
      >
        <span className={styles.docIcon} aria-hidden="true">
          📍
        </span>
        <span className={styles.docMeta}>
          <span className={styles.docName}>{loc?.name || "Location"}</span>
          {loc?.address ? <span className={styles.docSize}>{loc.address}</span> : null}
        </span>
      </a>
    );
  }
  if (m.type === "contacts") {
    const payload = m.payload as
      | {
          contacts?: Array<{
            name?: { formatted_name?: string };
            phones?: Array<{ phone?: string }>;
          }>;
        }
      | null
      | undefined;
    const first = payload?.contacts?.[0];
    return (
      <div className={styles.docCard}>
        <span className={styles.docIcon} aria-hidden="true">
          👤
        </span>
        <span className={styles.docMeta}>
          <span className={styles.docName}>
            {first?.name?.formatted_name ?? m.body ?? "Contact"}
          </span>
          {first?.phones?.[0]?.phone ? (
            <span className={styles.docSize}>{first.phones[0].phone}</span>
          ) : null}
        </span>
      </div>
    );
  }
  return null;
}

// One capture group → String.split alternates [text, url, text, url, …].
const URL_RE = /(https?:\/\/[^\s<>"']+|www\.[^\s<>"']+)/gi;

/** Chat text with URLs rendered as clickable links (new tab). Trailing sentence
 *  punctuation stays outside the link so "…thepisau.com." doesn't 404. */
function LinkifiedText({ text }: { text: string }) {
  const parts = text.split(URL_RE);
  if (parts.length === 1) return <>{text}</>;
  return (
    <>
      {parts.map((part, i) => {
        if (i % 2 === 0) return part;
        const [, url = part, tail = ""] = part.match(/^(.*?)([.,!?;:)]*)$/s) ?? [];
        return (
          // biome-ignore lint/suspicious/noArrayIndexKey: parts derive purely from `text` (stateless); the whole list re-renders when the text changes
          <span key={`${i}-${url}`}>
            <a
              href={url.startsWith("http") ? url : `https://${url}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {url}
            </a>
            {tail}
          </span>
        );
      })}
    </>
  );
}

function Bubble({
  m,
  onOpenImage,
  onReact,
  onMediaLoad,
}: {
  m: ThreadMessage;
  onOpenImage: (src: string) => void;
  onReact: (messageId: number, emoji: string) => void;
  onMediaLoad: () => void;
}) {
  // senderKind → them (incoming) / us (agent) / bot.
  const cls = m.direction === "out" ? (m.senderKind === "bot" ? "bot" : "us") : "them";
  const [picker, setPicker] = useState(false);
  const isSticker = (m.mediaKind ?? m.type) === "sticker";
  // Per-message CTWA ad context (0019): THIS message's ad — a repeat customer
  // may click a different ad than the one that first opened the thread.
  const adRef = (m.payload as { referral?: MessageAdReferral } | null | undefined)?.referral;
  const hasMedia =
    Boolean(m.mediaId) || Boolean(m.localUrl) || m.type === "location" || m.type === "contacts";
  const bubbleClass = [
    styles.bubble,
    styles[`bubble_${cls}` as keyof typeof styles] as string,
    isSticker ? styles.bubbleSticker : "",
    (m.mediaKind ?? m.type) === "image" && (m.mediaId || m.localUrl) ? styles.bubbleImage : "",
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <div
      data-dir={m.direction}
      className={`${styles.bubbleRow} ${styles[`row_${cls}` as keyof typeof styles] as string}`}
    >
      <div className={bubbleClass}>
        {cls === "bot" && <span className={styles.bubbleTag}>🤖 Bot</span>}
        {cls === "us" && m.agentName && <span className={styles.bubbleTag}>{m.agentName}</span>}
        {m.type === "template" && m.templateName && (
          <span className={styles.bubbleTag}>
            📣 {m.senderKind === "automation" ? "Broadcast · " : "Template · "}
            {m.templateName}
          </span>
        )}
        {adRef && (
          <div className={styles.bubbleAd}>
            {adRef.mediaId && (
              <button
                type="button"
                className={styles.bubbleAdThumbBtn}
                aria-label="View ad image"
                onClick={() => onOpenImage(`/api/v1/media/${adRef.mediaId}`)}
              >
                <img
                  className={styles.bubbleAdThumb}
                  src={`/api/v1/media/${adRef.mediaId}`}
                  alt="Ad creative"
                  onLoad={onMediaLoad}
                />
              </button>
            )}
            <span className={styles.bubbleAdText}>
              <span className={styles.bubbleAdLabel}>
                {/* An unmatched source_type=post is an ORGANIC page post (or a
                    boost from an unsynced ad account) — calling it an ad would
                    mislead ad-performance conversations. */}
                {adRef.sourceType === "post" && !adRef.adId ? "Via FB post" : "Via ad"}
                {adRef.campaignName ? ` · ${adRef.campaignName}` : ""}
              </span>
              <b>
                {adRef.adName ??
                  adRef.headline ??
                  (adRef.sourceType === "post" ? "Facebook post" : "Facebook/Instagram ad")}
              </b>
              {adRef.adName && adRef.headline ? <span>{adRef.headline}</span> : null}
              {adRef.sourceUrl && (
                <a
                  className={styles.bubbleAdLink}
                  href={adRef.sourceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {adRef.sourceType === "post" && !adRef.adId ? "View post ↗" : "View ad ↗"}
                </a>
              )}
            </span>
          </div>
        )}
        {hasMedia && <MediaContent m={m} onOpenImage={onOpenImage} onMediaLoad={onMediaLoad} />}
        {m.body && (
          <span className={styles.bubbleText}>
            <LinkifiedText text={m.body} />
          </span>
        )}
        {!hasMedia && !m.body && <span className={styles.bubbleText}>{`[${m.type}]`}</span>}
        <span className={styles.bubbleTime}>
          {fmtClock(m.createdAt)}
          {m.direction === "out" && <Ticks status={m.status} />}
        </span>
        {m.reactionEmoji && (
          <span
            className={`${styles.reactionBadge}${isSticker ? ` ${styles.reactionOnSticker}` : ""}`}
          >
            {m.reactionEmoji}
          </span>
        )}
      </div>
      {m.direction === "in" && (
        <div className={styles.reactWrap}>
          <button
            type="button"
            className={styles.reactBtn}
            aria-label="React to message"
            onClick={() => setPicker((o) => !o)}
          >
            ☺
          </button>
          {picker && (
            <div className={styles.reactPicker}>
              {REACTIONS.map((e) => (
                <button
                  type="button"
                  key={e}
                  className={styles.reactEmoji}
                  onClick={() => {
                    onReact(m.id, e);
                    setPicker(false);
                  }}
                >
                  {e}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Lightbox({ src, onClose }: { src: string; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: full-screen backdrop close
    // biome-ignore lint/a11y/useKeyWithClickEvents: Escape closes via the document keydown handler above
    <div className={styles.lightbox} onClick={onClose}>
      <img className={styles.lightboxImg} src={src} alt="Full size" />
    </div>
  );
}

/** One tile in the compose preview. `big` renders the single-file layout (large,
 *  playable); otherwise a small square thumbnail with a remove (×) affordance. */
function PreviewItem({
  file,
  url,
  big,
  onRemove,
}: {
  file: File;
  url: string;
  big: boolean;
  onRemove?: (() => void) | undefined;
}) {
  const isImg = file.type.startsWith("image/");
  const isVid = file.type.startsWith("video/");
  const media = isImg ? (
    <img className={big ? styles.previewImg : styles.previewTileImg} src={url} alt={file.name} />
  ) : isVid ? (
    <video
      className={big ? styles.previewImg : styles.previewTileImg}
      src={`${url}#t=0.1`}
      controls={big}
      muted
      playsInline
      preload="metadata"
    />
  ) : (
    <div className={big ? styles.previewDoc : styles.previewTileDoc}>
      <span aria-hidden="true">📄</span>
      <span className={styles.docName}>{file.name}</span>
      {big && <span className={styles.docSize}>{humanSize(file.size)}</span>}
    </div>
  );
  if (big) return media;
  return (
    <div className={styles.previewTile}>
      {media}
      {onRemove && (
        <button
          type="button"
          className={styles.previewRemove}
          onClick={onRemove}
          aria-label={`Remove ${file.name}`}
        >
          ×
        </button>
      )}
    </div>
  );
}

/** Toggle labels on a conversation + create new ones on the fly, plus per-label
 *  management: inline rename and a two-step delete (removes it from every chat). */
function LabelsModal({
  onClose,
  allLabels,
  applied,
  onToggle,
  onApply,
  onRename,
  onDelete,
}: {
  onClose: () => void;
  allLabels: LabelRow[];
  applied: string[];
  onToggle: (name: string) => void;
  onApply: (name: string) => void;
  onRename: (id: number, name: string) => void;
  onDelete: (id: number) => void;
}) {
  const [draft, setDraft] = useState("");
  const [renamingId, setRenamingId] = useState<number | null>(null);
  const [renameDraft, setRenameDraft] = useState("");
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const add = () => {
    const n = draft.trim();
    if (!n) return;
    onApply(n);
    setDraft("");
  };
  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: backdrop click-to-close
    <div className={styles.previewScrim} onMouseDown={onClose}>
      {/* biome-ignore lint/a11y/noStaticElementInteractions: modal card; stops backdrop close */}
      <div className={styles.sheetCard} onMouseDown={(e) => e.stopPropagation()}>
        <div className={styles.sheetHead}>Labels</div>
        <div className={styles.labelList}>
          {allLabels.length === 0 && <div className={styles.sheetHint}>No labels yet.</div>}
          {allLabels.map((l) => {
            const on = applied.includes(l.name);
            if (renamingId === l.id) {
              return (
                <div key={l.id} className={styles.labelRenameRow}>
                  <input
                    className={styles.sheetInput}
                    value={renameDraft}
                    maxLength={40}
                    // biome-ignore lint/a11y/noAutofocus: row only exists after an explicit edit click
                    autoFocus
                    onChange={(e) => setRenameDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && renameDraft.trim()) {
                        onRename(l.id, renameDraft.trim());
                        setRenamingId(null);
                      }
                      if (e.key === "Escape") setRenamingId(null);
                    }}
                  />
                  <button
                    type="button"
                    className={styles.btnGhost}
                    disabled={!renameDraft.trim()}
                    onClick={() => {
                      onRename(l.id, renameDraft.trim());
                      setRenamingId(null);
                    }}
                  >
                    Save
                  </button>
                </div>
              );
            }
            return (
              <div key={l.id} className={styles.labelRow}>
                <button
                  type="button"
                  className={[styles.labelOpt, on ? styles.labelOptOn : ""]
                    .filter(Boolean)
                    .join(" ")}
                  onClick={() => onToggle(l.name)}
                >
                  <span className={styles.labelCheck}>{on ? "✓" : ""}</span>
                  <span className={styles.labelOptName}>{l.name}</span>
                </button>
                <button
                  type="button"
                  className={styles.labelTool}
                  title="Rename label"
                  aria-label={`Rename ${l.name}`}
                  onClick={() => {
                    setRenameDraft(l.name);
                    setRenamingId(l.id);
                    setDeletingId(null);
                  }}
                >
                  ✎
                </button>
                <button
                  type="button"
                  className={[styles.labelTool, deletingId === l.id ? styles.labelToolDanger : ""]
                    .filter(Boolean)
                    .join(" ")}
                  title={deletingId === l.id ? "Click again to delete everywhere" : "Delete label"}
                  aria-label={`Delete ${l.name}`}
                  onClick={() => {
                    if (deletingId === l.id) {
                      onDelete(l.id);
                      setDeletingId(null);
                    } else {
                      setDeletingId(l.id);
                      setRenamingId(null);
                    }
                  }}
                >
                  {deletingId === l.id ? "Sure?" : "✕"}
                </button>
              </div>
            );
          })}
        </div>
        <div className={styles.labelNewRow}>
          <input
            className={styles.sheetInput}
            placeholder="New label…"
            value={draft}
            maxLength={40}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") add();
            }}
          />
          <button type="button" className={styles.btnGhost} onClick={add}>
            Add
          </button>
        </div>
        <div className={styles.previewActions}>
          <button type="button" className={styles.btnSend} onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

/** Log an order by hand + optionally fire the order_confirmation / tracking_notification
 *  utility templates. Tracking sends need all of courier+number+URL (Meta rejects empty
 *  template params). Shows a free (open 24h window) vs charged hint. */
function LogOrderModal({
  windowOpen,
  submitting,
  onClose,
  onSubmit,
  onOpenPanel,
}: {
  windowOpen: boolean;
  submitting: boolean;
  onClose: () => void;
  onSubmit: (body: LogOrderBody) => Promise<void>;
  onOpenPanel: () => void;
}) {
  const [total, setTotal] = useState("");
  const [orderId, setOrderId] = useState("");
  const [sendConfirm, setSendConfirm] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const amount = Number(total);
  const canSubmit = Number.isFinite(amount) && amount > 0;

  async function submit() {
    if (!canSubmit) return;
    setErr(null);
    const body: LogOrderBody = {
      totalPrice: amount,
      sendConfirmation: sendConfirm,
      ...(orderId.trim() ? { orderId: orderId.trim() } : {}),
    };
    try {
      await onSubmit(body);
      onClose();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Could not log the order");
    }
  }

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: backdrop click-to-close
    <div className={styles.previewScrim} onMouseDown={onClose}>
      {/* biome-ignore lint/a11y/noStaticElementInteractions: modal card; stops backdrop close */}
      <div className={styles.sheetCard} onMouseDown={(e) => e.stopPropagation()}>
        <div className={styles.sheetHead}>Log order</div>
        <label className={styles.field}>
          <span className={styles.fieldLbl}>Total (MYR)</span>
          <input
            className={styles.sheetInput}
            type="number"
            inputMode="decimal"
            min="0"
            step="0.01"
            value={total}
            onChange={(e) => setTotal(e.target.value)}
            placeholder="0.00"
          />
        </label>
        <label className={styles.field}>
          <span className={styles.fieldLbl}>Order ID (optional)</span>
          <input
            className={styles.sheetInput}
            value={orderId}
            onChange={(e) => setOrderId(e.target.value)}
            placeholder="Auto-generated if blank"
            maxLength={64}
          />
        </label>
        <label className={styles.checkRow}>
          <input
            type="checkbox"
            checked={sendConfirm}
            onChange={(e) => setSendConfirm(e.target.checked)}
          />
          <span>
            Send order confirmation{" "}
            {sendConfirm &&
              (windowOpen ? (
                <em className={styles.freeTag}>free · window open</em>
              ) : (
                <em className={styles.chargeTag}>charged · window closed</em>
              ))}
          </span>
        </label>
        <button type="button" className={styles.sheetLink} onClick={onOpenPanel}>
          Add tracking later — open the customer panel →
        </button>

        {err && <p className={styles.err}>{err}</p>}
        <div className={styles.previewActions}>
          <button type="button" className={styles.btnGhost} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className={styles.btnSend}
            onClick={submit}
            disabled={!canSubmit || submitting}
          >
            {submitting ? "Saving…" : "Log order"}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Add tracking to an existing order (manual, Woo, or Shopify) + optionally send the
 *  tracking_notification. Sending needs courier + number + URL (Meta rejects empty params). */
function TrackingModal({
  order,
  windowOpen,
  submitting,
  onClose,
  onSubmit,
}: {
  order: { orderRef: string; orderNumber: string | null };
  windowOpen: boolean;
  submitting: boolean;
  onClose: () => void;
  onSubmit: (body: AddTrackingBody) => Promise<void>;
}) {
  const [number, setNumber] = useState("");
  const [courier, setCourier] = useState("");
  const [url, setUrl] = useState("");
  const [send, setSend] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const canSubmit = number.trim() !== "" && (!send || (courier.trim() !== "" && url.trim() !== ""));

  async function submit() {
    if (!canSubmit) return;
    setErr(null);
    try {
      await onSubmit({
        orderRef: order.orderRef,
        ...(order.orderNumber ? { orderNumber: order.orderNumber } : {}),
        number: number.trim(),
        ...(courier.trim() ? { courier: courier.trim() } : {}),
        ...(url.trim() ? { url: url.trim() } : {}),
        send,
      });
      onClose();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Could not add tracking");
    }
  }

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: backdrop click-to-close
    <div className={styles.previewScrim} onMouseDown={onClose}>
      {/* biome-ignore lint/a11y/noStaticElementInteractions: modal card; stops backdrop close */}
      <div className={styles.sheetCard} onMouseDown={(e) => e.stopPropagation()}>
        <div className={styles.sheetHead}>Tracking · {order.orderNumber ?? order.orderRef}</div>
        <label className={styles.field}>
          <span className={styles.fieldLbl}>Tracking number</span>
          <input
            className={styles.sheetInput}
            value={number}
            onChange={(e) => setNumber(e.target.value)}
            maxLength={80}
          />
        </label>
        <label className={styles.field}>
          <span className={styles.fieldLbl}>Courier</span>
          <input
            className={styles.sheetInput}
            value={courier}
            onChange={(e) => setCourier(e.target.value)}
            placeholder="e.g. PosLaju"
            maxLength={60}
          />
        </label>
        <label className={styles.field}>
          <span className={styles.fieldLbl}>Tracking URL</span>
          <input
            className={styles.sheetInput}
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://…"
            maxLength={300}
          />
        </label>
        <label className={styles.checkRow}>
          <input type="checkbox" checked={send} onChange={(e) => setSend(e.target.checked)} />
          <span>
            Send tracking update{" "}
            {send &&
              (windowOpen ? (
                <em className={styles.freeTag}>free · window open</em>
              ) : (
                <em className={styles.chargeTag}>charged · window closed</em>
              ))}
          </span>
        </label>
        {send && (
          <p className={styles.sheetHint}>Courier + number + URL are all required to send.</p>
        )}
        {err && <p className={styles.err}>{err}</p>}
        <div className={styles.previewActions}>
          <button type="button" className={styles.btnGhost} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className={styles.btnSend}
            onClick={submit}
            disabled={!canSubmit || submitting}
          >
            {submitting ? "Saving…" : "Save tracking"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Thread({
  businessId,
  convo,
  onBack,
  infoActive,
  onInfo,
}: {
  businessId: string;
  convo: ConversationRow;
  onBack: () => void;
  infoActive: boolean;
  onInfo: () => void;
}) {
  const thread = useThread(convo.id);
  const send = useSendMessage(businessId, convo.id);
  const templates = useTemplates();
  const [draft, setDraft] = useState("");
  const [tplId, setTplId] = useState<number | "">("");
  const [err, setErr] = useState<string | null>(null);
  const [attachOpen, setAttachOpen] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const attachWrapRef = useRef<HTMLDivElement>(null);
  const actionsWrapRef = useRef<HTMLDivElement>(null);
  const emojiWrapRef = useRef<HTMLDivElement>(null);
  const draftRef = useRef<HTMLTextAreaElement>(null);
  const upload = useUploadMedia(businessId);
  const react = useReact(convo.id);
  const qc = useQueryClient();
  // Labels (create-on-the-fly picker) + manual order logging.
  const labelsQ = useLabels(businessId);
  const applyLabel = useApplyLabel(businessId, convo.id);
  const removeLabel = useRemoveLabel(businessId, convo.id);
  const renameLabel = useRenameLabel(businessId);
  const deleteLabel = useDeleteLabel(businessId);
  const logOrder = useLogOrder(businessId, convo.id);
  const [labelsOpen, setLabelsOpen] = useState(false);
  const [orderOpen, setOrderOpen] = useState(false);
  const appliedLabels = convo.labels ?? [];
  // Compose tray: one or more picked files (WhatsApp-style multi-select) + a shared
  // caption. Each item keeps its own object-URL for the thumbnail preview.
  const [pending, setPending] = useState<{
    items: PendingMedia[];
    caption: string;
  } | null>(null);
  // Monotonic negative ids for optimistic messages injected into the thread cache —
  // negative so they never collide with real (positive) server ids or React keys.
  const tempIdRef = useRef(-1);
  const captionRef = useRef<HTMLTextAreaElement>(null);
  const mediaSendingRef = useRef(false);
  const [mediaSending, setMediaSending] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [lightbox, setLightbox] = useState<string | null>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const docInputRef = useRef<HTMLInputElement>(null);
  // Whether the user is scrolled to the bottom — media loading re-pins only then,
  // so late-loading images/videos don't yank the view while reading history.
  const atBottomRef = useRef(true);

  const messages = thread.data?.messages ?? [];
  const win = thread.data?.window;
  const open = win?.open ?? convo.window_open ?? false;
  const capHours = win?.capHours ?? convo.cap_hours ?? 24;
  const hoursLeft = useMemo(() => {
    if (!win?.closesAt) return null;
    const ms = new Date(win.closesAt).getTime() - Date.now();
    return ms > 0 ? Math.max(1, Math.round(ms / 3_600_000)) : 0;
  }, [win?.closesAt]);
  // Image-header templates are sendable 1:1 when they carry a saved default
  // image (0018) — the server attaches it. Default-less ones stay hidden: the
  // composer has no image picker and Meta rejects a bare send (132012).
  const approved = (templates.data ?? []).filter(
    (t) =>
      t.status === "approved" && (t.headerFormat !== "image" || t.defaultHeaderMediaId != null),
  );

  // Pin to the bottom on new messages / thread switch. useLayoutEffect runs before
  // paint (no top-flash), and a follow-up rAF catches height that settles after the
  // frame (fonts/layout) so a chat reliably opens at the latest message.
  // biome-ignore lint/correctness/useExhaustiveDependencies: pin-to-bottom is keyed on message count + thread id, not the ref.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
    atBottomRef.current = true;
    const id = requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight;
    });
    return () => cancelAnimationFrame(id);
  }, [messages.length, convo.id]);

  // Re-pin to the bottom when media finishes loading (and grows the thread), but
  // only if the user was already at the bottom — prevents the "jumpy" scroll.
  function onMediaLoad() {
    const el = scrollRef.current;
    if (el && atBottomRef.current) el.scrollTop = el.scrollHeight;
  }

  useEffect(() => {
    if (!attachOpen) return;
    const onDown = (e: MouseEvent) => {
      if (attachWrapRef.current && !attachWrapRef.current.contains(e.target as Node))
        setAttachOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [attachOpen]);

  useEffect(() => {
    if (!actionsOpen) return;
    const onDown = (e: MouseEvent) => {
      if (actionsWrapRef.current && !actionsWrapRef.current.contains(e.target as Node))
        setActionsOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [actionsOpen]);

  useEffect(() => {
    if (!emojiOpen) return;
    const onDown = (e: MouseEvent) => {
      if (emojiWrapRef.current && !emojiWrapRef.current.contains(e.target as Node))
        setEmojiOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setEmojiOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [emojiOpen]);

  // Auto-grow the reply box with its content (capped in CSS via max-height) —
  // scrollHeight is only correct after resetting height, hence the two writes.
  // With NO draft, keep the single-line default: scrollHeight would otherwise
  // measure the WRAPPED placeholder (textarea placeholders wrap, inputs clip),
  // ballooning the box on narrow screens — e.g. the closed-window state on
  // mobile, where the template picker squeezes this box thin (client bug report
  // 2026-07-29).
  useLayoutEffect(() => {
    const el = draftRef.current;
    if (!el) return;
    if (!open || !draft) {
      el.style.height = "";
      return;
    }
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight + 2}px`;
  }, [draft, open]);

  useLayoutEffect(() => {
    const el = captionRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight + 2}px`;
  }, [pending?.caption]);

  // Insert at the caret (falling back to append) so picking mid-sentence works.
  function insertEmoji(emoji: string) {
    const el = draftRef.current;
    const start = el?.selectionStart ?? draft.length;
    const end = el?.selectionEnd ?? start;
    setDraft(draft.slice(0, start) + emoji + draft.slice(end));
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      const pos = start + emoji.length;
      el.setSelectionRange(pos, pos);
    });
  }

  // Toggle a label on this conversation (applied names come from convo.labels).
  function toggleLabel(name: string) {
    if (appliedLabels.includes(name)) {
      const id = labelsQ.data?.find((l) => l.name === name)?.id;
      if (id != null) removeLabel.mutate(id);
    } else {
      applyLabel.mutate(name);
    }
  }

  async function sendText() {
    if (!draft.trim()) return;
    setErr(null);
    try {
      await send.mutateAsync({ text: draft.trim() });
      setDraft("");
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Send failed");
    }
  }
  async function sendTemplate() {
    if (tplId === "") return;
    setErr(null);
    try {
      await send.mutateAsync({ templateId: Number(tplId), values: {} });
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Send failed");
    }
  }

  async function chooseFiles(list?: FileList | File[] | null) {
    if (!list || mediaSendingRef.current) return;
    const picked = Array.from(list);
    if (picked.length === 0) return;
    setAttachOpen(false);
    setErr(null);
    const items: PendingMedia[] = [];
    const skipped: string[] = [];
    for (const f of picked) {
      const ready = await normalizeImageOrientation(f);
      // Reject types WhatsApp can't send up-front, instead of uploading then failing
      // at Meta (which used to make the bubble vanish).
      if (unsupportedMediaReason(ready.type)) {
        skipped.push(ready.name);
        continue;
      }
      items.push({ file: ready, url: URL.createObjectURL(ready) });
    }
    if (items.length === 0) {
      setErr(unsupportedMediaReason(picked[0]?.type) ?? "That file type can't be sent.");
      return;
    }
    if (skipped.length > 0) {
      setErr(`Skipped ${skipped.length} unsupported file${skipped.length > 1 ? "s" : ""}.`);
    }
    setPending({ items, caption: "" });
  }

  /** Send one file: inject a single optimistic bubble that transitions in place
   *  (blob → served copy), upload, then persist. Isolated per file so a multi-send
   *  loop shows every picture immediately and one failure doesn't sink the rest. */
  async function sendOneMedia(item: PendingMedia, caption: string) {
    const { file, url: blobUrl } = item;
    const key = ["thread", convo.id] as const;
    const tempId = tempIdRef.current--;
    const kind = kindOf(file.type);
    const patch = (fn: (m: ThreadMessage) => ThreadMessage) =>
      qc.setQueryData<{ window: WindowState; messages: ThreadMessage[] }>(key, (old) =>
        old ? { ...old, messages: old.messages.map((m) => (m.id === tempId ? fn(m) : m)) } : old,
      );
    const optimistic: ThreadMessage = {
      id: tempId,
      direction: "out",
      senderKind: "agent",
      type: kind,
      mediaKind: kind,
      mediaFilename: file.name,
      mediaSize: file.size,
      body: caption.trim() || null,
      status: "sending",
      createdAt: new Date().toISOString(),
      mediaId: null,
      localUrl: blobUrl,
      uploadProgress: 0,
    };
    qc.setQueryData<{ window: WindowState; messages: ThreadMessage[] }>(key, (old) =>
      old ? { ...old, messages: [...old.messages, optimistic] } : old,
    );
    try {
      // A retry after a failed send reuses the stored upload instead of re-uploading
      // (and re-converting) the file, which would orphan the first media row.
      const mediaId =
        item.mediaId ??
        (
          await upload.mutateAsync({
            file,
            onProgress: (pct) => patch((m) => ({ ...m, uploadProgress: pct })),
          })
        ).id;
      item.mediaId = mediaId;
      // Bytes are on the server the moment the upload resolves, so for images preload
      // the served copy before swapping — the in-place src change is then instant.
      if (kind === "image") {
        await new Promise<void>((resolve) => {
          const img = new Image();
          img.onload = () => resolve();
          img.onerror = () => resolve();
          img.src = `/api/v1/media/${mediaId}`;
        });
      }
      // Swap the SAME element from the local blob to the served copy (cached above),
      // drop the ring. Same id → same <img>, so the picture never blinks.
      patch((m) => ({
        ...m,
        mediaId,
        localUrl: null,
        uploadProgress: null,
        status: "accepted",
      }));
      // Persist + let the send's onSuccess refetch reconcile the temp id → real id.
      await send.mutateAsync({
        mediaId,
        ...(caption.trim() ? { caption: caption.trim() } : {}),
      });
    } catch (e) {
      // Drop this optimistic bubble on failure; surface the reason.
      qc.setQueryData<{ window: WindowState; messages: ThreadMessage[] }>(key, (old) =>
        old ? { ...old, messages: old.messages.filter((m) => m.id !== tempId) } : old,
      );
      throw e;
    }
  }

  async function confirmSendMedia() {
    if (!pending || mediaSendingRef.current) return;
    mediaSendingRef.current = true;
    setMediaSending(true);
    const { items, caption } = pending;
    setErr(null);
    const failed: typeof items = [];
    let captionFailed = false;
    // Successful files are removed from the retry tray so retry never resends them.
    try {
      for (let i = 0; i < items.length; i++) {
        const it = items[i];
        if (!it) continue;
        try {
          await sendOneMedia(it, i === 0 ? caption : "");
          URL.revokeObjectURL(it.url);
        } catch (e) {
          failed.push(it);
          if (i === 0) captionFailed = true;
          setErr(e instanceof Error ? e.message : "Send failed");
        }
      }
      setPending(failed.length ? { items: failed, caption: captionFailed ? caption : "" } : null);
      if (failed.length > 0 && items.length > 1)
        setErr(`${failed.length} of ${items.length} didn't send.`);
    } finally {
      mediaSendingRef.current = false;
      setMediaSending(false);
    }
  }

  function cancelPending() {
    if (mediaSendingRef.current) return;
    if (pending) for (const it of pending.items) URL.revokeObjectURL(it.url);
    setPending(null);
  }
  function removePendingItem(idx: number) {
    setPending((p) => {
      if (!p) return p;
      const it = p.items[idx];
      if (it) URL.revokeObjectURL(it.url);
      const items = p.items.filter((_, i) => i !== idx);
      return items.length ? { ...p, items } : null;
    });
  }
  function onReact(messageId: number, emoji: string) {
    react.mutate({ messageId, emoji });
  }

  // When a custom name is the headline, surface the customer's own WhatsApp
  // name underneath (the "~ name" convention from the WhatsApp Business app).
  const waTilde =
    convo.name && convo.wa_name && convo.wa_name !== convo.name ? `~ ${convo.wa_name} · ` : "";
  const subhead = `${waTilde}${convo.segment ? `${convo.phone} · ${convo.segment}` : convo.phone}`;

  return (
    <div className={styles.thread}>
      <div className={styles.threadHead}>
        <button
          type="button"
          className={styles.threadBack}
          onClick={onBack}
          aria-label="Back to list"
        >
          {Ic.back}
        </button>
        <Avatar size={36} name={convo.name ?? convo.wa_name} seed={convo.phone} />
        <div className={styles.threadId}>
          <div className={styles.threadName}>{convo.name ?? convo.wa_name ?? convo.phone}</div>
          <div className={styles.threadPhone}>{subhead}</div>
          {(convo.is_ctwa || convo.referral_ad_name) && (
            <div
              className={styles.threadRef}
              title={[
                "From ad",
                convo.referral_campaign,
                convo.referral_ad_name,
                convo.referral_headline,
              ]
                .filter(Boolean)
                .join(" · ")}
            >
              {convo.referral_media_id ? (
                <img
                  className={styles.threadRefThumb}
                  src={`/api/v1/media/${convo.referral_media_id}`}
                  alt="Ad creative"
                />
              ) : (
                Ic.adperf
              )}
              <span>
                From ad
                {convo.referral_campaign ? (
                  <>
                    {" · "}
                    <b>{convo.referral_campaign}</b>
                  </>
                ) : null}
                {convo.referral_ad_name ? <> · {convo.referral_ad_name}</> : null}
                {convo.referral_headline ? <> — “{convo.referral_headline}”</> : null}
              </span>
              {convo.referral_source_url && (
                <a
                  className={styles.threadRefLink}
                  href={convo.referral_source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Open the ad post"
                >
                  View ad ↗
                </a>
              )}
            </div>
          )}
        </div>
        <div className={styles.threadTools}>
          <button
            type="button"
            className={[styles.threadTool, infoActive ? styles.infoTglOn : ""]
              .filter(Boolean)
              .join(" ")}
            aria-label="Customer info"
            title="Customer info"
            onClick={onInfo}
          >
            {Ic.contacts}
          </button>
          <button
            type="button"
            className={`${styles.threadTool} ${styles.dkOnly}`}
            aria-label="Log order"
            title="Log order"
            onClick={() => setOrderOpen(true)}
          >
            {Ic.receipt}
          </button>
          <button
            type="button"
            className={`${styles.threadTool} ${styles.dkOnly}`}
            aria-label="Labels"
            title="Labels"
            onClick={() => setLabelsOpen(true)}
          >
            {Ic.tag}
          </button>
          <div className={`${styles.threadToolWrap} ${styles.mbOnly}`} ref={actionsWrapRef}>
            <button
              type="button"
              className={[styles.threadTool, actionsOpen ? styles.threadToolOn : ""]
                .filter(Boolean)
                .join(" ")}
              aria-label="Actions"
              aria-expanded={actionsOpen}
              onClick={() => setActionsOpen((o) => !o)}
            >
              {Ic.kebab}
            </button>
            {actionsOpen && (
              <div className={styles.actionsMenu} role="menu">
                <button
                  type="button"
                  className={styles.menuItem}
                  onClick={() => {
                    setActionsOpen(false);
                    setLabelsOpen(true);
                  }}
                >
                  {Ic.tag}
                  <span className={styles.menuLbl}>Label this chat</span>
                  <span className={styles.menuGo}>›</span>
                </button>
                <button
                  type="button"
                  className={styles.menuItem}
                  onClick={() => {
                    setActionsOpen(false);
                    setOrderOpen(true);
                  }}
                >
                  {Ic.receipt}
                  <span className={styles.menuLbl}>Log order</span>
                  <span className={styles.menuGo}>›</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* biome-ignore lint/a11y/noStaticElementInteractions: drag-and-drop drop zone for media */}
      <div
        className={[styles.threadBody, dragOver ? styles.threadBodyDrag : ""]
          .filter(Boolean)
          .join(" ")}
        ref={scrollRef}
        onScroll={(e) => {
          const el = e.currentTarget;
          atBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
        }}
        onDragOver={(e) => {
          if (!open) return;
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          if (open) chooseFiles(e.dataTransfer.files);
        }}
      >
        {dateGrouped(messages).map(({ date, msgs }) => (
          <div key={date} className={styles.dayGroup}>
            <div className={styles.threadDay}>{date}</div>
            {msgs.map((m) =>
              m.type === "order_event" ? (
                <div className={styles.threadOrder} key={m.id}>
                  {Ic.receipt}
                  <span>Order logged{m.body ? ` · ${m.body}` : ""}</span>
                </div>
              ) : (
                <Bubble
                  key={m.id}
                  m={m}
                  onOpenImage={setLightbox}
                  onReact={onReact}
                  onMediaLoad={onMediaLoad}
                />
              ),
            )}
          </div>
        ))}
        {messages.length === 0 && <p className={styles.noMsg}>No messages yet.</p>}
        {dragOver && <div className={styles.dropHint}>Drop to send</div>}
      </div>

      <div className={styles.composer}>
        <div className={[styles.winStrip, open ? styles.winOpen : styles.winClosed].join(" ")}>
          {Ic.clock}
          {open ? (
            <span>
              24-hour window <b>open</b>
              {hoursLeft != null ? ` · ${hoursLeft}h left` : ""}
              {capHours === 72 ? " · CTWA 72h" : ""} — free-form replies OK
            </span>
          ) : (
            <span>
              Window <b>closed</b> — reopen with an approved <b>template</b>
            </span>
          )}
        </div>

        <div className={styles.composerIn}>
          <div className={styles.attachWrap} ref={attachWrapRef}>
            <button
              type="button"
              className={[styles.composerAttach, attachOpen ? styles.composerAttachOn : ""]
                .filter(Boolean)
                .join(" ")}
              aria-label="Attach"
              aria-expanded={attachOpen}
              disabled={!open}
              title={open ? "Attach" : "Window closed"}
              onClick={() => setAttachOpen((o) => !o)}
            >
              {Ic.paperclip}
            </button>
            {attachOpen && (
              <div className={styles.attachMenu} role="menu">
                <button
                  type="button"
                  className={styles.menuItem}
                  onClick={() => photoInputRef.current?.click()}
                >
                  <span aria-hidden="true">🖼️</span>
                  <span className={styles.menuLbl}>Photo or Video</span>
                </button>
                <button
                  type="button"
                  className={styles.menuItem}
                  onClick={() => docInputRef.current?.click()}
                >
                  <span aria-hidden="true">📄</span>
                  <span className={styles.menuLbl}>Document</span>
                </button>
              </div>
            )}
            <input
              ref={photoInputRef}
              type="file"
              accept="image/*,video/*,audio/*"
              multiple
              hidden
              onChange={(e) => {
                chooseFiles(e.target.files);
                e.target.value = "";
              }}
            />
            <input
              ref={docInputRef}
              type="file"
              multiple
              hidden
              onChange={(e) => {
                chooseFiles(e.target.files);
                e.target.value = "";
              }}
            />
          </div>
          {/* Hidden (not just disabled) when the window is closed: the closed
              state also squeezes in the template picker, and on mobile every
              extra button starves the text box of width. */}
          {open && (
            <div className={styles.emojiWrap} ref={emojiWrapRef}>
              <button
                type="button"
                className={[styles.composerAttach, emojiOpen ? styles.composerAttachOn : ""]
                  .filter(Boolean)
                  .join(" ")}
                aria-label="Emoji"
                aria-expanded={emojiOpen}
                title="Emoji"
                onClick={() => setEmojiOpen((o) => !o)}
              >
                <span aria-hidden="true" className={styles.emojiBtnFace}>
                  🙂
                </span>
              </button>
              {emojiOpen && <EmojiPicker onPick={insertEmoji} />}
            </div>
          )}
          <textarea
            ref={draftRef}
            className={styles.composerText}
            rows={1}
            placeholder={open ? "Type a reply…" : "Window closed — send an approved template"}
            value={open ? draft : ""}
            disabled={!open}
            onChange={(e) => setDraft(e.target.value)}
            onPaste={(e) => {
              const files = e.clipboardData.files;
              if (open && files && files.length > 0) {
                e.preventDefault();
                chooseFiles(files);
              }
            }}
            onKeyDown={(e) => {
              // Enter sends; Shift+Enter inserts a line break (multi-line
              // messages — R-INBOX composer). IME confirmation is not a send.
              if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
                e.preventDefault();
                sendText();
              }
            }}
          />
          {!open && (
            <select
              className={styles.tplSelect}
              value={tplId}
              onChange={(e) => setTplId(e.target.value ? Number(e.target.value) : "")}
              aria-label="Approved template"
            >
              <option value="">Template…</option>
              {approved.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.displayName ?? t.name}
                </option>
              ))}
            </select>
          )}
          <button
            type="button"
            className={styles.btnSend}
            disabled={open ? send.isPending || !draft.trim() : tplId === "" || send.isPending}
            onClick={open ? sendText : sendTemplate}
          >
            {Ic.send} Send
          </button>
        </div>

        {/* Per-business quick replies are Phase-4; the row renders empty for now. */}
        <div className={styles.quickRow} />
      </div>
      {err && !pending && <p className={styles.err}>{err}</p>}

      {pending && (
        // biome-ignore lint/a11y/noStaticElementInteractions: backdrop click-to-close
        <div className={styles.previewScrim} onMouseDown={cancelPending}>
          {/* biome-ignore lint/a11y/noStaticElementInteractions: modal card; stops backdrop close */}
          <div className={styles.previewCard} onMouseDown={(e) => e.stopPropagation()}>
            <div className={pending.items.length > 1 ? styles.previewGrid : styles.previewBody}>
              {pending.items.map((it, i) => (
                <PreviewItem
                  key={it.url}
                  file={it.file}
                  url={it.url}
                  big={pending.items.length === 1}
                  onRemove={
                    pending.items.length > 1 && !mediaSending
                      ? () => removePendingItem(i)
                      : undefined
                  }
                />
              ))}
            </div>
            <textarea
              ref={captionRef}
              rows={1}
              maxLength={1024}
              aria-label="Media caption"
              disabled={mediaSending}
              className={styles.previewCaption}
              placeholder="Add a caption…"
              value={pending.caption}
              onChange={(e) => setPending((p) => (p ? { ...p, caption: e.target.value } : p))}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
                  e.preventDefault();
                  confirmSendMedia();
                }
              }}
            />
            {err && (
              <p role="alert" className={styles.err}>
                {err}
              </p>
            )}
            <div className={styles.previewActions}>
              <button
                type="button"
                className={styles.btnGhost}
                disabled={mediaSending}
                onClick={cancelPending}
              >
                Cancel
              </button>
              <button
                type="button"
                className={styles.btnSend}
                disabled={mediaSending}
                onClick={confirmSendMedia}
              >
                {Ic.send} {mediaSending ? "Sending…" : "Send"}
              </button>
            </div>
          </div>
        </div>
      )}
      {lightbox && <Lightbox src={lightbox} onClose={() => setLightbox(null)} />}
      {labelsOpen && (
        <LabelsModal
          onClose={() => setLabelsOpen(false)}
          allLabels={labelsQ.data ?? []}
          applied={appliedLabels}
          onToggle={toggleLabel}
          onApply={(name) => applyLabel.mutate(name)}
          onRename={(id, name) => renameLabel.mutate({ id, name })}
          onDelete={(id) => deleteLabel.mutate(id)}
        />
      )}
      {orderOpen && (
        <LogOrderModal
          windowOpen={open}
          submitting={logOrder.isPending}
          onClose={() => setOrderOpen(false)}
          onSubmit={(body) => logOrder.mutateAsync(body).then(() => undefined)}
          onOpenPanel={() => {
            setOrderOpen(false);
            if (!infoActive) onInfo();
          }}
        />
      )}
    </div>
  );
}

/* Customer-context rail (doc 01 §3.11) — Phase-4. The data payload (orders,
   spend, source, region, recent orders, audiences) is not wired yet, so the
   rail renders the documented empty states from whatever the row already
   carries. Built so it degrades gracefully and never crashes on missing fields. */
function ContactProfile({ convo, businessId }: { convo: ConversationRow; businessId: string }) {
  const ordersQ = useOrders(convo.id);
  const orders = ordersQ.data ?? [];
  const spend = orders.reduce((s, o) => s + (Number(o.amount) || 0), 0);
  const labels = convo.labels ?? [];
  const addTracking = useAddTracking(convo.id);
  const rename = useRenameContact(businessId);
  const updateOrder = useUpdateOrder(businessId, convo.id);
  const deleteOrder = useDeleteOrder(businessId, convo.id);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [trackFor, setTrackFor] = useState<{
    orderRef: string;
    orderNumber: string | null;
  } | null>(null);
  const [editOrder, setEditOrder] = useState<OrderRow | null>(null);
  const displayName = convo.name ?? convo.wa_name;

  async function saveName() {
    const v = nameDraft.trim();
    await rename.mutateAsync({ id: convo.contact_id, name: v || null });
    setEditingName(false);
  }

  return (
    <div className={styles.cprofile}>
      <div className={styles.cprofileId}>
        {initialsFor(displayName) ? (
          <div
            className={`${styles.cprofileAv} ${styles.avInitials}`}
            style={avatarStyle(convo.phone)}
          >
            {initialsFor(displayName)}
          </div>
        ) : (
          <div className={styles.cprofileAv}>{Ic.personFill}</div>
        )}
        {editingName ? (
          <div className={styles.nameEditRow}>
            <input
              className={styles.nameEditInput}
              value={nameDraft}
              placeholder={convo.wa_name ?? "Custom name"}
              maxLength={120}
              // biome-ignore lint/a11y/noAutofocus: the row only exists after an explicit edit click
              autoFocus
              onChange={(e) => setNameDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") saveName();
                if (e.key === "Escape") setEditingName(false);
              }}
            />
            <button
              type="button"
              className={styles.nameEditSave}
              disabled={rename.isPending}
              onClick={saveName}
            >
              Save
            </button>
          </div>
        ) : (
          <button
            type="button"
            className={styles.nameEditBtn}
            title="Set a custom name"
            onClick={() => {
              setNameDraft(convo.name ?? "");
              setEditingName(true);
            }}
          >
            <span className={styles.cprofileName}>{displayName ?? convo.phone}</span>
            <span className={styles.namePencil} aria-hidden="true">
              ✎
            </span>
          </button>
        )}
        {convo.name && convo.wa_name && convo.wa_name !== convo.name && (
          <div className={styles.cprofileWaName}>~ {convo.wa_name} · WhatsApp name</div>
        )}
        <div className={styles.cprofilePhone}>{convo.phone}</div>
        <div className={styles.cprofileTags}>
          {convo.segment && <span className={styles.segPill}>{convo.segment}</span>}
          <span
            className={[styles.optin, convo.optin ? styles.optinYes : styles.optinNo]
              .filter(Boolean)
              .join(" ")}
          >
            {convo.optin ? "Opted-in" : "Not opted-in"}
          </span>
        </div>
      </div>

      <div className={styles.cprofileStats}>
        {/* Prefer the contact's trigger-maintained totals (all conversations, net of
            cancellations/refunds) over summing this thread's last-20 created rows. */}
        <div>
          <span className={styles.cpsK}>Orders</span>
          <span className={styles.cpsV}>{convo.orders_count ?? orders.length ?? "—"}</span>
        </div>
        <div>
          <span className={styles.cpsK}>Spend</span>
          <span className={styles.cpsV}>
            {convo.total_spend != null
              ? `RM${Math.round(Number(convo.total_spend))}`
              : orders.length
                ? `RM${Math.round(spend)}`
                : "—"}
          </span>
        </div>
        <div>
          <span className={styles.cpsK}>Source</span>
          <span className={styles.cpsV}>{convo.source_store ?? "—"}</span>
        </div>
        <div>
          <span className={styles.cpsK}>Region</span>
          <span className={styles.cpsV}>{convo.region ?? "—"}</span>
        </div>
      </div>

      {labels.length > 0 && (
        <div className={styles.cprofileSec}>
          <div className={styles.cprofileSecH}>{Ic.tag} Labels</div>
          <div className={styles.convLabels}>
            {labels.map((l) => (
              <span className={styles.convLabel} key={l}>
                {l}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className={styles.cprofileSec}>
        <div className={styles.cprofileSecH}>{Ic.receipt} Recent orders</div>
        {orders.length === 0 ? (
          <div className={styles.cprofileEmpty}>No orders yet · new customer.</div>
        ) : (
          <div className={styles.orderList}>
            {orders.map((o) => (
              <div className={styles.orderRow} key={o.id}>
                <span className={styles.orderNo}>{o.order_number ?? o.order_ref}</span>
                {o.amount != null && (
                  <span className={styles.orderAmt}>
                    {o.currency} {Number(o.amount).toFixed(2)}
                  </span>
                )}
                <button
                  type="button"
                  className={styles.orderTrackBtn}
                  onClick={() =>
                    setTrackFor({ orderRef: o.order_ref, orderNumber: o.order_number })
                  }
                >
                  {o.tracking_number ? "Update tracking" : "Add tracking"}
                </button>
                {o.source === "manual" && (
                  <button
                    type="button"
                    className={styles.orderTrackBtn}
                    title="Edit or remove this order"
                    onClick={() => setEditOrder(o)}
                  >
                    Edit
                  </button>
                )}
                {o.tracking_number && (
                  <span className={styles.orderTrack}>📦 {o.tracking_number}</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className={styles.cprofileSec}>
        <div className={styles.cprofileSecH}>{Ic.contacts} Audiences</div>
        <div className={styles.cprofileEmpty}>Not in any audience.</div>
      </div>

      {trackFor && (
        <TrackingModal
          order={trackFor}
          windowOpen={convo.window_open ?? false}
          submitting={addTracking.isPending}
          onClose={() => setTrackFor(null)}
          onSubmit={(body) => addTracking.mutateAsync(body).then(() => undefined)}
        />
      )}
      {editOrder && (
        <EditOrderModal
          order={editOrder}
          submitting={updateOrder.isPending || deleteOrder.isPending}
          onClose={() => setEditOrder(null)}
          onSave={(body) =>
            updateOrder.mutateAsync({ id: editOrder.id, ...body }).then(() => undefined)
          }
          onDelete={() => deleteOrder.mutateAsync(editOrder.id).then(() => undefined)}
        />
      )}
    </div>
  );
}

/** Edit a manually-logged order (amount / display number) or remove it entirely.
 *  Store-synced orders never reach here — their Edit button isn't rendered. */
function EditOrderModal({
  order,
  submitting,
  onClose,
  onSave,
  onDelete,
}: {
  order: OrderRow;
  submitting: boolean;
  onClose: () => void;
  onSave: (body: { totalPrice?: number; orderNumber?: string }) => Promise<void>;
  onDelete: () => Promise<void>;
}) {
  const [total, setTotal] = useState(order.amount != null ? Number(order.amount).toFixed(2) : "");
  const [orderNumber, setOrderNumber] = useState(order.order_number ?? "");
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const amount = Number(total);
  const canSave = Number.isFinite(amount) && amount > 0 && !submitting;

  async function save() {
    if (!canSave) return;
    setErr(null);
    try {
      await onSave({ totalPrice: amount, orderNumber: orderNumber.trim() });
      onClose();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Could not update the order");
    }
  }
  async function remove() {
    setErr(null);
    try {
      await onDelete();
      onClose();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Could not remove the order");
    }
  }

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: backdrop click-to-close
    <div className={styles.previewScrim} onMouseDown={onClose}>
      {/* biome-ignore lint/a11y/noStaticElementInteractions: modal card; stops backdrop close */}
      <div className={styles.sheetCard} onMouseDown={(e) => e.stopPropagation()}>
        <div className={styles.sheetHead}>Edit order · {order.order_number ?? order.order_ref}</div>
        <label className={styles.field}>
          <span className={styles.fieldLbl}>Total ({order.currency})</span>
          <input
            className={styles.sheetInput}
            type="number"
            inputMode="decimal"
            min="0"
            step="0.01"
            value={total}
            onChange={(e) => setTotal(e.target.value)}
          />
        </label>
        <label className={styles.field}>
          <span className={styles.fieldLbl}>Order ID</span>
          <input
            className={styles.sheetInput}
            value={orderNumber}
            onChange={(e) => setOrderNumber(e.target.value)}
            maxLength={80}
          />
        </label>
        <p className={styles.sheetHint}>
          Edits adjust this customer's spend and segment automatically. Messages already sent are
          not affected.
        </p>
        {err && <p className={styles.err}>{err}</p>}
        <div className={styles.previewActions}>
          <button
            type="button"
            className={[styles.btnGhost, confirmingDelete ? styles.btnDanger : ""]
              .filter(Boolean)
              .join(" ")}
            disabled={submitting}
            onClick={() => (confirmingDelete ? remove() : setConfirmingDelete(true))}
          >
            {confirmingDelete ? "Confirm remove" : "Remove order"}
          </button>
          <button type="button" className={styles.btnGhost} onClick={onClose}>
            Cancel
          </button>
          <button type="button" className={styles.btnSend} disabled={!canSave} onClick={save}>
            {submitting ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function dateGrouped(msgs: ThreadMessage[]): Array<{ date: string; msgs: ThreadMessage[] }> {
  const groups: Array<{ date: string; msgs: ThreadMessage[] }> = [];
  for (const m of msgs) {
    const date = new Date(m.createdAt).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      timeZone: "Asia/Kuala_Lumpur",
    });
    const g = groups.at(-1);
    if (g && g.date === date) g.msgs.push(m);
    else groups.push({ date, msgs: [m] });
  }
  return groups;
}
